import { inject, Pipe, PipeTransform } from '@angular/core';
import { LocaleService } from '@maximizer/core/shared/data-access';
import { TranslateService } from '@ngx-translate/core';
import { AiMarkupUtils } from '../../classes/ai-markup-utils';
import {
  DATA_ATTRIBUTES,
  escapeHtmlAttribute,
} from '../../pipes/ref-link.utils';
import {
  CrmCitation,
  CrmCitationRecord,
  crmCitationIcon,
  crmCitationTypeKey,
  readCrmCitation,
} from '../citations/crm-citation.model';

export const CRM_CITATION_CLASS = 'crm-citation';

// A citation inside a code block or inline code is the reader's text, and a chip inside a link
// would put a button in an anchor.
const UNTOUCHED_PATTERN =
  /(<pre[\s\S]*?<\/pre>|<code\b[\s\S]*?<\/code>|<a\b[\s\S]*?<\/a>)/i;

// A record cited twice in one paragraph, bullet or cell keeps its first chip only.
const BLOCK_END_PATTERN = /(<\/(?:p|li|td|th|h[1-6]|blockquote|dd|dt)>)/i;

// A citation still arriving is hidden until it closes, so no half-written key shows.
const OPEN_CITATION_START = '(Maximizer:';
const CLOSING_TAGS_TAIL_PATTERN = /(?:<\/[a-z][a-z0-9]*>\s*)*$/i;

const CHIP_CLASSES =
  'inline-flex items-center gap-1 align-middle max-w-[18rem] px-1.5 py-px m-0 rounded border border-solid border-neutral-20 bg-neutral-5 text-caption leading-4 text-neutral-90 no-underline cursor-pointer whitespace-nowrap transition-colors hover:bg-bush-green-15 hover:border-neutral-40 focus:outline-none focus-visible:ring-2 focus-visible:ring-finserv-100';

/**
 * Turns the `(Maximizer: { ... })` citations of a v2 answer into record chips: icon, subject and
 * date from the CRM record, or the type and date the answer wrote until the record is read.
 */
@Pipe({ name: 'crmCitationRef' })
export class CrmCitationRefPipe implements PipeTransform {
  private readonly translate = inject(TranslateService);
  private readonly locale = inject(LocaleService);

  transform(
    value: string | null | undefined,
    records?: ReadonlyMap<string, CrmCitationRecord> | null,
    streaming = false,
  ): string {
    if (!value) {
      return '';
    }

    const html = streaming ? hideOpenCitation(value) : value;
    if (!html.includes('Maximizer:')) {
      return html;
    }

    return html
      .split(UNTOUCHED_PATTERN)
      .map((part, index) =>
        index % 2 === 1 ? part : this.renderBlocks(part, records ?? null),
      )
      .join('');
  }

  private renderBlocks(
    html: string,
    records: ReadonlyMap<string, CrmCitationRecord> | null,
  ): string {
    return html
      .split(BLOCK_END_PATTERN)
      .map((chunk, index) =>
        index % 2 === 1 ? chunk : this.renderChunk(chunk, records),
      )
      .join('');
  }

  private renderChunk(
    html: string,
    records: ReadonlyMap<string, CrmCitationRecord> | null,
  ): string {
    const seen = new Set<string>();

    return AiMarkupUtils.replaceMaximizerReferences(html, (params) => {
      const citation = readCrmCitation(params);
      if (!citation || seen.has(citation.key)) {
        return null;
      }
      seen.add(citation.key);

      return this.renderChip(citation, records?.get(citation.key) ?? null);
    });
  }

  private renderChip(
    citation: CrmCitation,
    record: CrmCitationRecord | null,
  ): string {
    const kind =
      record?.interaction ||
      this.translate.instant(crmCitationTypeKey(citation.type));
    const date = formatCitationDate(
      this.locale,
      record?.date ?? citation.date,
      record?.hasTime ?? false,
    );
    const subject = record?.subject ?? '';

    // With a subject on show, the type is still read out before it.
    const typePrefix = subject
      ? `<span class="sr-only">${escapeHtmlAttribute(kind)}: </span>`
      : '';
    const dateText = date
      ? `<span class="crm-citation-date text-neutral-70">· ${escapeHtmlAttribute(date)}</span>`
      : '';

    return (
      `<button type="button" class="${CRM_CITATION_CLASS} ${CHIP_CLASSES}"${citationAttributes(citation)}>` +
      `<span class="crm-citation-icon ${escapeHtmlAttribute(crmCitationIcon(citation))} icon-10 text-neutral-70 shrink-0" aria-hidden="true"></span>` +
      typePrefix +
      `<span class="crm-citation-text min-w-0 truncate">${escapeHtmlAttribute(subject || kind)}</span>` +
      dateText +
      `</button>`
    );
  }
}

/**
 * The date a chip or card shows: the reader's short date, with the time of day for a timed
 * appointment or task. A date in a form the locale cannot read shows as written.
 */
export function formatCitationDate(
  locale: LocaleService,
  value: Date | string | undefined,
  withTime: boolean,
): string {
  if (!value) {
    return '';
  }

  try {
    const formatted = locale.formatDate(
      value,
      withTime ? 'userDateTime' : 'userDate',
    );
    return formatted.trim() || String(value);
  } catch {
    return typeof value === 'string' ? value : '';
  }
}

function citationAttributes(citation: CrmCitation): string {
  const attributes: [string, string | undefined][] = [
    [DATA_ATTRIBUTES.KEY, citation.key],
    [DATA_ATTRIBUTES.TYPE, citation.type],
    [DATA_ATTRIBUTES.WITH_ID, citation.withKey],
    [DATA_ATTRIBUTES.WITH_TYPE, citation.withType],
    [DATA_ATTRIBUTES.INTERACTION_DIRECTION, citation.interactionDirection],
    [DATA_ATTRIBUTES.INTERACTION_TYPE, citation.interactionType],
    [DATA_ATTRIBUTES.INTERACTION_EXTERNAL, citation.interactionExternal],
    [DATA_ATTRIBUTES.DATE, citation.date],
  ];

  return attributes
    .filter((entry): entry is [string, string] => Boolean(entry[1]))
    .map(([name, value]) => ` ${name}="${escapeHtmlAttribute(value)}"`)
    .join('');
}

function hideOpenCitation(html: string): string {
  const tail = CLOSING_TAGS_TAIL_PATTERN.exec(html)?.[0] ?? '';
  const body = html.slice(0, html.length - tail.length);
  // The first citation opened after the last one closed is the one still arriving.
  const start = body.indexOf(OPEN_CITATION_START, body.lastIndexOf(')') + 1);

  return start === -1 ? html : body.slice(0, start).trimEnd() + tail;
}
